import { formatDistanceToNow } from "date-fns";
import { Pencil, Plus } from "lucide-react";
import type { RouterOutputs } from "@/trpc";

export type CandidateNoteSummary =
  RouterOutputs["jobPost"]["candidateNoteSummaries"][number];

const UNKNOWN_AUTHOR_LABEL = "Unknown";
const FOCUS_RING_CLASS_NAME =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary";
// Extends the tappable area to 44px without adding vertical space to the
// compact empty-state row.
const EMPTY_HIT_AREA_CLASS_NAME =
  "relative after:absolute after:inset-x-0 after:-inset-y-3 after:content-['']";

function formatAuthorName(
  latestNote: CandidateNoteSummary["latestNote"],
): string {
  const name =
    `${latestNote.authorFirstName ?? ""} ${latestNote.authorLastName ?? ""}`.trim();
  return name || UNKNOWN_AUTHOR_LABEL;
}

const LABEL_CLASS_NAME =
  "text-[11px] font-medium uppercase tracking-wide text-slate-400";

/**
 * Full-width row of the candidate card's field grid for internal notes. It
 * keeps the same label/value structure as the other fields so adding the first
 * note only grows the value line instead of swapping in a different control.
 * Must be rendered inside the card's `<dl>`.
 */
export function CandidateNotesField({
  summary,
  displayName,
  onOpen,
}: {
  summary: CandidateNoteSummary | undefined;
  displayName: string;
  onOpen: () => void;
}) {
  if (!summary) {
    return (
      <div className="col-span-2 min-w-0">
        <dt className={LABEL_CLASS_NAME}>Notes</dt>
        <dd className="mt-1">
          <button
            type="button"
            onClick={onOpen}
            aria-label={`Add a note for ${displayName}`}
            className={`${EMPTY_HIT_AREA_CLASS_NAME} inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-primary hover:underline ${FOCUS_RING_CLASS_NAME}`}
          >
            <Plus aria-hidden="true" size={14} className="shrink-0" />
            Add note
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div className="col-span-2 min-w-0">
      <dt className={LABEL_CLASS_NAME}>
        Notes <span className="tabular-nums">· {summary.count}</span>
      </dt>
      <dd className="mt-1">
        <button
          type="button"
          onClick={onOpen}
          aria-label={`View and edit notes for ${displayName}`}
          className={`group block min-h-11 w-full min-w-0 rounded-md text-left ${FOCUS_RING_CLASS_NAME}`}
        >
          <span className="line-clamp-2 whitespace-pre-line text-sm leading-5 text-slate-700 [overflow-wrap:anywhere]">
            {summary.latestNote.preview}
          </span>
          <span className="mt-0.5 flex items-center justify-between gap-2 text-xs text-slate-400">
            <span className="min-w-0 truncate">
              {formatAuthorName(summary.latestNote)} ·{" "}
              {formatDistanceToNow(new Date(summary.latestNote.createdAt), {
                addSuffix: true,
              })}
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 font-medium text-primary group-hover:underline">
              <Pencil aria-hidden="true" size={12} />
              Edit
            </span>
          </span>
        </button>
      </dd>
    </div>
  );
}
